import { clsx } from "clsx";
import type {
  ButtonHTMLAttributes,
  CSSProperties,
  MouseEvent,
  RefObject,
} from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { AudioLevelIndicator } from "../AudioLevelIndicator/AudioLevelIndicator";
import { Icon, type IconName } from "../Icons";
import { Heading } from "../Typography/Heading";
import "./FloatingProcessButton.css";

export interface FloatingProcessButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Text shown in the idle state
   * @default "Start dictation"
   */
  inactiveText?: string;

  /**
   * Text shown in the active state
   * @default "Stop dictation"
   */
  activeText?: string;

  /**
   * Icon name shown in the idle state
   * @default "microphone"
   */
  inactiveIconName?: IconName;

  /**
   * Icon name shown in the active state
   * @default "stop"
   */
  activeIconName?: IconName;

  /**
   * Show waveform indicator when active (replaces the active icon)
   * @default false
   */
  showWaveformWhenActive?: boolean;

  /**
   * Audio level for waveform (0-1). If not provided, a simulated level can be used.
   */
  audioLevel?: number;

  /**
   * Simulate waveform levels when audioLevel is not provided
   * @default true
   */
  simulateWaveform?: boolean;

  /**
   * Callback fired when switching from idle to active
   */
  onActivate?: () => void;

  /**
   * Callback fired when switching from active to idle
   */
  onDeactivate?: () => void;

  /**
   * Controlled active state
   */
  isActive?: boolean;

  /**
   * Default active state for uncontrolled usage
   * @default false
   */
  defaultActive?: boolean;

  /**
   * Icon size in pixels
   * @default 20
   */
  iconSize?: number;

  /**
   * Expanded width in pixels
   * @default "fit-content"
   */
  expandedWidth?: number | "fit-content";

  /**
   * Fixed placement on the screen
   * @default "right-bottom"
   */
  position?:
    | "left-top"
    | "left-center"
    | "left-bottom"
    | "right-top"
    | "right-center"
    | "right-bottom";

  /**
   * Margin from screen edges in pixels
   * @default 30
   */
  screenMargin?: number;

  /**
   * Optional container element ref to constrain positioning within.
   * When provided, the button positions relative to the container's
   * edges instead of the viewport edges.
   */
  containerRef?: RefObject<HTMLElement | null>;
}

type CssVars = Record<`--${string}`, string>;

export const FloatingProcessButton = forwardRef<
  HTMLButtonElement,
  FloatingProcessButtonProps
>(
  (
    {
      inactiveText = "Start dictation",
      activeText = "Stop dictation",
      inactiveIconName = "microphone",
      activeIconName = "stop",
      showWaveformWhenActive = false,
      audioLevel,
      simulateWaveform = true,
      onActivate,
      onDeactivate,
      isActive,
      defaultActive = false,
      iconSize = 20,
      expandedWidth = "fit-content",
      position = "right-bottom",
      screenMargin = 30,
      containerRef,
      className = "",
      disabled = false,
      type,
      onClick,
      ...props
    },
    ref,
  ) => {
    const [internalActive, setInternalActive] = useState(defaultActive);
    const active = isActive ?? internalActive;
    const [side, vertical] = position.split("-") as [
      "left" | "right",
      "top" | "center" | "bottom",
    ];
    const textRef = useRef<HTMLHeadingElement>(null);
    const [textWidth, setTextWidth] = useState(0);
    const [simulatedLevel, setSimulatedLevel] = useState(0.2);
    const showWaveform = active && showWaveformWhenActive;
    const shouldSimulate =
      showWaveform && simulateWaveform && audioLevel === undefined;

    // Container-relative positioning
    const [containerPos, setContainerPos] = useState<number | null>(null);

    const updateContainerPos = useCallback(() => {
      const el = containerRef?.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (side === "right") {
        setContainerPos(window.innerWidth - rect.right + screenMargin);
      } else {
        setContainerPos(rect.left + screenMargin);
      }
    }, [containerRef, side, screenMargin]);

    useLayoutEffect(() => {
      const el = containerRef?.current;
      if (!el) {
        setContainerPos(null);
        return;
      }

      updateContainerPos();

      const observer = new ResizeObserver(updateContainerPos);
      observer.observe(el);
      window.addEventListener("resize", updateContainerPos);

      return () => {
        observer.disconnect();
        window.removeEventListener("resize", updateContainerPos);
      };
    }, [containerRef, updateContainerPos]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
    useLayoutEffect(() => {
      const node = textRef.current;
      if (!node) {
        return;
      }

      const updateWidth = () => {
        setTextWidth(node.scrollWidth || 0);
      };

      updateWidth();

      if (typeof ResizeObserver === "undefined") {
        return;
      }

      const observer = new ResizeObserver(() => updateWidth());
      observer.observe(node);

      return () => observer.disconnect();
    }, [activeText, inactiveText]);

    useEffect(() => {
      if (!shouldSimulate) {
        return;
      }

      const interval = window.setInterval(() => {
        setSimulatedLevel(0.15 + Math.random() * 0.75);
      }, 120);

      return () => window.clearInterval(interval);
    }, [shouldSimulate]);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);

      if (event.defaultPrevented || disabled) {
        return;
      }

      if (active) {
        onDeactivate?.();
        if (isActive === undefined) {
          setInternalActive(false);
        }
      } else {
        onActivate?.();
        if (isActive === undefined) {
          setInternalActive(true);
        }
      }
    };

    const styleVars: CssVars = {
      "--floating-process-text-width": `${textWidth}px`,
      "--floating-process-icon-size": `${iconSize}px`,
      "--floating-process-screen-margin": `${screenMargin}px`,
    };

    if (typeof expandedWidth === "number") {
      styleVars["--floating-process-expanded-width"] = `${expandedWidth}px`;
    }

    // Container-relative position override
    const positionOverride: CSSProperties = {};
    if (containerPos !== null) {
      positionOverride[side] = containerPos;
    }

    return (
      <button
        ref={ref}
        type={type ?? "button"}
        className={clsx(
          "indigo-floating-process-button",
          { "is-active": active, "is-waveform": showWaveform },
          `is-${side}`,
          `is-${vertical}`,
          className,
        )}
        onClick={handleClick}
        disabled={disabled}
        aria-pressed={active}
        style={
          {
            ...(props.style ?? {}),
            ...styleVars,
            ...positionOverride,
          } as CSSProperties
        }
        {...props}
      >
        <span
          className="indigo-floating-process-button-ring"
          aria-hidden="true"
        />
        {showWaveform ? (
          <>
            <AudioLevelIndicator
              level={audioLevel ?? simulatedLevel}
              isActive={showWaveform}
              variant="waveform"
            />
            <Heading
              ref={textRef}
              level={3}
              className="indigo-floating-process-button-text"
            >
              {activeText}
            </Heading>
          </>
        ) : (
          <>
            <span className="indigo-floating-process-button-icon-wrap">
              <Icon
                name={active ? activeIconName : inactiveIconName}
                sizeX={iconSize}
                sizeY={iconSize}
                className="indigo-floating-process-button-icon"
              />
            </span>
            <Heading
              ref={textRef}
              level={3}
              className="indigo-floating-process-button-text"
            >
              {active ? activeText : inactiveText}
            </Heading>
          </>
        )}
      </button>
    );
  },
);

FloatingProcessButton.displayName = "FloatingProcessButton";

export default FloatingProcessButton;
